/**
 * UsageStats - AI token usage and cost breakdown for a run or a ticket (admin only)
 */

import { DollarSign } from 'lucide-react';
import { Card } from '../common';
import type { UsageSummary } from '@resolve-ai/shared';
import { formatCost } from '../../lib/format-utils';

interface UsageStatsProps {
  usage: UsageSummary | null;
  isLoading?: boolean;
  error?: string | null;
}

function formatTokens(tokens: number): string {
  return tokens.toLocaleString('en-US');
}

function formatCategoryLabel(category: string): string {
  return category
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function UsageStats({ usage, isLoading, error }: UsageStatsProps) {
  if (isLoading) {
    return (
      <Card>
        <span className="text-sm text-gray-500 dark:text-gray-400">Loading usage...</span>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      </Card>
    );
  }

  if (!usage || usage.byCategory.length === 0) {
    return null;
  }

  return (
    <Card>
      <div className="flex items-center gap-3 mb-4">
        <DollarSign className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">AI Usage</h3>
      </div>

      <div className="flex items-center flex-wrap gap-x-6 gap-y-2 mb-4 pb-4 border-b border-gray-200/60 dark:border-gray-700/50">
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Total Cost</p>
          <p className="text-lg font-semibold text-gray-900 dark:text-white">
            {formatCost(usage.totalCostCents)}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Input Tokens</p>
          <p className="text-lg font-semibold text-gray-900 dark:text-white">
            {formatTokens(usage.totalInputTokens)}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Output Tokens</p>
          <p className="text-lg font-semibold text-gray-900 dark:text-white">
            {formatTokens(usage.totalOutputTokens)}
          </p>
        </div>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
            <th className="font-medium pb-2">Phase</th>
            <th className="font-medium pb-2">Model</th>
            <th className="font-medium pb-2 text-right">Input</th>
            <th className="font-medium pb-2 text-right">Output</th>
            <th className="font-medium pb-2 text-right">Cost</th>
          </tr>
        </thead>
        <tbody>
          {usage.byCategory.map((row) => (
            <tr
              key={`${row.category}-${row.model}`}
              className="border-t border-gray-100 dark:border-gray-700"
            >
              <td className="py-1.5 text-gray-900 dark:text-white">
                {formatCategoryLabel(row.category)}
              </td>
              <td className="py-1.5 text-gray-600 dark:text-gray-400">{row.model}</td>
              <td className="py-1.5 text-right text-gray-600 dark:text-gray-400">
                {formatTokens(row.inputTokens)}
              </td>
              <td className="py-1.5 text-right text-gray-600 dark:text-gray-400">
                {formatTokens(row.outputTokens)}
              </td>
              <td className="py-1.5 text-right text-gray-900 dark:text-white">
                {formatCost(row.costCents)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
